/**
 * 空间索引管理器 - 使用网格分区优化大量对象的查询性能
 *
 * 【设计目标】
 * 解决 Fabric.js 遍历所有对象的性能问题（O(n) -> O(k)，k为视口内对象数）
 *
 * 【核心原理】
 * 将画布划分为固定大小的网格，每个对象根据其位置存入对应网格
 * 查询时只需遍历视口覆盖的网格，而不是所有对象
 *
 * 【性能提升】
 * - 1000 个对象，视口内 100 个：
 *   - 原方案：遍历 1000 个对象
 *   - 空间索引：只遍历约 100 个对象
 *   - 性能提升：约 10 倍
 *
 * 【使用场景】
 * 1. 视口变换后只更新可见对象的坐标
 * 2. 点击检测时快速定位目标对象
 * 3. 渲染优化时只绘制可见区域的对象
 */

import { fabric } from 'fabric';

/**
 * 边界框接口
 */
export interface BBox {
  left: number;
  top: number;
  width: number;
  height: number;
  right?: number;
  bottom?: number;
}

/**
 * 网格单元键类型
 */
type CellKey = string;

/**
 * 空间索引管理器
 *
 * @example
 * ```ts
 * const spatialIndex = new SpatialIndexManager(800);
 *
 * // 添加对象时更新索引
 * canvas.on('object:added', (e) => {
 *   spatialIndex.addObject(e.target);
 * });
 *
 * // 拖拽画布时只更新可见对象
 * const viewport = getViewportBounds();
 * const visibleObjects = spatialIndex.getVisibleObjects(viewport);
 * visibleObjects.forEach(obj => obj.setCoords());
 * ```
 */
export class SpatialIndexManager {
  /**
   * 网格大小（像素）
   * 建议值：800-2048，支持 4K 图片(4096x4096)时避免单对象覆盖过多网格
   */
  private readonly gridSize: number;

  /**
   * 网格存储
   * Key: "x,y" 格式的网格坐标
   * Value: 该网格内的对象集合
   */
  private readonly grid = new Map<CellKey, Set<fabric.Object>>();

  /**
   * 对象到网格的映射
   * 用于快速找到对象所在的网格（移除时使用）
   */
  private readonly objectToCells = new Map<fabric.Object, Set<CellKey>>();

  /**
   * 是否启用调试模式
   */
  private debugMode = false;

  constructor(gridSize: number = 800) {
    this.gridSize = gridSize;
  }

  /**
   * 添加对象到索引
   *
   * @param obj Fabric.js 对象
   * @param skipUpdate 是否跳过已存在对象的位置更新（默认会先移除旧位置）
   */
  addObject(obj: fabric.Object, skipUpdate = false): void {
    if (!skipUpdate && this.objectToCells.has(obj)) {
      this.removeObject(obj);
    }

    const bounds = this.getObjectBounds(obj);
    if (!bounds) return;

    const cells = this.getBoundsCells(bounds);

    // ⚠️ 性能警告：对象覆盖过多网格
    if (cells.size > 50 && this.debugMode) {
      console.warn(
        `[SpatialIndex] 对象 ${obj.id || 'unknown'} 覆盖了 ${cells.size} 个网格，`,
        `边界：`, bounds,
        `\n建议：调整网格大小或检查对象尺寸`
      );
    }

    // 添加到网格
    for (const cellKey of cells) {
      if (!this.grid.has(cellKey)) {
        this.grid.set(cellKey, new Set());
      }
      this.grid.get(cellKey)!.add(obj);
    }

    // 记录映射关系
    this.objectToCells.set(obj, new Set(cells));

    if (this.debugMode) {
      console.log(`[SpatialIndex] 添加对象 ${obj.id || 'unknown'}，覆盖 ${cells.size} 个网格`);
    }
  }

  /**
   * 从索引中移除对象
   *
   * @param obj Fabric.js 对象
   */
  removeObject(obj: fabric.Object): void {
    const cells = this.objectToCells.get(obj);
    if (!cells) return;

    // 从所有网格中移除
    for (const cellKey of cells) {
      const cell = this.grid.get(cellKey);
      if (cell) {
        cell.delete(obj);
        // 如果网格为空，删除该网格
        if (cell.size === 0) {
          this.grid.delete(cellKey);
        }
      }
    }

    // 删除映射关系
    this.objectToCells.delete(obj);

    if (this.debugMode) {
      console.log(`[SpatialIndex] 移除对象 ${obj.id || 'unknown'}`);
    }
  }

  /**
   * 更新对象位置（对象移动后调用）
   *
   * @param obj Fabric.js 对象
   */
  updateObject(obj: fabric.Object): void {
    this.addObject(obj, false);
  }

  /**
   * 批量添加对象
   *
   * @param objects Fabric.js 对象数组
   */
  addObjects(objects: fabric.Object[]): void {
    for (const obj of objects) {
      this.addObject(obj);
    }
  }

  /**
   * 获取视口内的所有对象
   *
   * @param viewport 视口边界框
   * @returns 视口内的对象数组（去重）
   */
  getVisibleObjects(viewport: BBox): fabric.Object[] {
    const cells = this.getBoundsCells(viewport);
    const objects = new Set<fabric.Object>();

    // 遍历视口覆盖的所有网格
    for (const cellKey of cells) {
      const cellObjects = this.grid.get(cellKey);
      if (cellObjects) {
        cellObjects.forEach(obj => objects.add(obj));
      }
    }

    const result = Array.from(objects);

    if (this.debugMode) {
      console.log(
        `[SpatialIndex] 查询视口对象：覆盖 ${cells.size} 个网格，找到 ${result.length} 个对象`
      );
    }

    return result;
  }

  /**
   * 获取指定点附近的对象（用于点击检测）
   *
   * @param point 点击点坐标
   * @param radius 检测半径（默认 10px）
   * @returns 附近的对象数组
   */
  getObjectsNearPoint(point: { x: number; y: number }, radius = 10): fabric.Object[] {
    return this.getVisibleObjects({
      left: point.x - radius,
      top: point.y - radius,
      width: radius * 2,
      height: radius * 2,
    });
  }

  /**
   * 清空索引
   */
  clear(): void {
    this.grid.clear();
    this.objectToCells.clear();

    if (this.debugMode) {
      console.log('[SpatialIndex] 清空索引');
    }
  }

  /**
   * 获取索引统计信息
   */
  getStats(): {
    totalObjects: number;
    totalCells: number;
    avgObjectsPerCell: number;
    maxObjectsPerCell: number;
  } {
    let totalCells = this.grid.size;
    let totalObjects = this.objectToCells.size;
    let maxObjects = 0;
    let sumObjects = 0;

    for (const cell of this.grid.values()) {
      const size = cell.size;
      sumObjects += size;
      if (size > maxObjects) maxObjects = size;
    }

    return {
      totalObjects,
      totalCells,
      avgObjectsPerCell: totalCells > 0 ? sumObjects / totalCells : 0,
      maxObjectsPerCell: maxObjects,
    };
  }

  /**
   * 启用/禁用调试模式
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * 获取对象的边界框
   */
  private getObjectBounds(obj: fabric.Object): BBox | null {
    try {
      // 对于有坐标的对象，计算边界
      if (obj.left !== undefined && obj.top !== undefined) {
        const width = obj.width ? obj.width * (obj.scaleX || 1) : 100;
        const height = obj.height ? obj.height * (obj.scaleY || 1) : 100;

        return {
          left: obj.left,
          top: obj.top,
          width,
          height,
          right: obj.left + width,
          bottom: obj.top + height,
        };
      }
    } catch (e) {
      console.warn('[SpatialIndex] 获取对象边界失败:', e);
    }

    return null;
  }

  /**
   * 计算边界框覆盖的网格单元
   */
  private getBoundsCells(bounds: BBox): Set<CellKey> {
    const cells = new Set<CellKey>();

    // 计算边界框覆盖的网格范围
    const startX = Math.floor(bounds.left / this.gridSize);
    const endX = Math.ceil((bounds.left + bounds.width) / this.gridSize);
    const startY = Math.floor(bounds.top / this.gridSize);
    const endY = Math.ceil((bounds.top + bounds.height) / this.gridSize);

    // 遍历所有覆盖的网格
    for (let x = startX; x <= endX; x++) {
      for (let y = startY; y <= endY; y++) {
        cells.add(`${x},${y}`);
      }
    }

    return cells;
  }

  /**
   * 可视化网格（用于调试）
   * 在画布上绘制网格线
   */
  visualizeGrid(canvas: fabric.Canvas): void {
    const vpt = canvas.viewportTransform;
    if (!vpt) return;

    const width = canvas.getWidth();
    const height = canvas.getHeight();

    // 计算视口在场景坐标中的范围
    const viewportLeft = -vpt[4] / vpt[0];
    const viewportTop = -vpt[5] / vpt[3];
    const viewportRight = viewportLeft + width / vpt[0];
    const viewportBottom = viewportTop + height / vpt[3];

    // 绘制网格线
    const lines: fabric.Line[] = [];

    // 垂直线
    const startX = Math.floor(viewportLeft / this.gridSize) * this.gridSize;
    for (let x = startX; x <= viewportRight; x += this.gridSize) {
      const line = new fabric.Line([x, viewportTop, x, viewportBottom], {
        stroke: 'rgba(255, 0, 0, 0.3)',
        strokeWidth: 1,
        selectable: false,
        evented: false,
      });
      lines.push(line);
    }

    // 水平线
    const startY = Math.floor(viewportTop / this.gridSize) * this.gridSize;
    for (let y = startY; y <= viewportBottom; y += this.gridSize) {
      const line = new fabric.Line([viewportLeft, y, viewportRight, y], {
        stroke: 'rgba(255, 0, 0, 0.3)',
        strokeWidth: 1,
        selectable: false,
        evented: false,
      });
      lines.push(line);
    }

    // 临时添加到画布（调试用）
    canvas.add(...lines);

    // 3 秒后移除
    setTimeout(() => {
      canvas.remove(...lines);
      canvas.requestRenderAll();
    }, 3000);
  }
}

/**
 * 创建空间索引的工厂函数
 *
 * @param canvas Fabric.js 画布实例
 * @param gridSize 网格大小（像素）
 * @returns 空间索引管理器实例
 */
export function createSpatialIndex(canvas: fabric.Canvas, gridSize = 800): SpatialIndexManager {
  const index = new SpatialIndexManager(gridSize);

  // 监听对象添加事件
  canvas.on('object:added', (e) => {
    if (e.target && e.target.id !== 'workspace') {
      index.addObject(e.target);
    }
  });

  // 监听对象移除事件
  canvas.on('object:removed', (e) => {
    if (e.target) {
      index.removeObject(e.target);
    }
  });

  // 监听对象修改事件（位置、大小变化）
  canvas.on('object:modified', (e) => {
    if (e.target) {
      index.updateObject(e.target);
    }
  });

  // 监听对象移动事件（拖拽时）
  canvas.on('object:moving', (e) => {
    if (e.target) {
      index.updateObject(e.target);
    }
  });

  // 初始化现有对象
  const existingObjects = canvas.getObjects().filter(obj => obj.id !== 'workspace');
  index.addObjects(existingObjects);

  return index;
}
